'use client'
import { useEffect } from 'react'

import styled from 'styled-components'
import Collapsible from '../components/Account/Collapsible'
import { BannerSection } from '../components/ContactUs/BannerSection'
import { MarketingSection } from '../components/ContactUs/MarketingSection'
import { desktopVw } from '../styles/utils'
import richTextRenderer from '../utils/richTextRenderer'

import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import styles from './ContactUs.module.scss'

const Section = styled.section``

const ChatFAQContainer = styled.div`
  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    display: flex;
    flex-direction: column;
  }
`

const FAQWrapper = styled.div`
  margin: 0;
  padding: 50px 30px;

  background-color: ${props => props.theme.colors.offWhite};

  h1,
  p,
  a {
    font-family: ${({ theme }) => theme.fonts.grosa};
  }
  h1 {
    text-align: center;
    font-size: 22px;
    padding-bottom: 51px;
    border-bottom: 1px solid ${props => props.theme.colors.primary};
  }

  h3 {
    font-family: ${({ theme }) => theme.fonts.ivyHeadline};
    font-size: 16px;
    line-height: 22px;
    margin: 20px 0;
  }

  p {
    font-size: 13px;
    line-height: 19px;
    margin: 2px 0 20px;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    width: 100%;
    padding: ${desktopVw(100)} ${desktopVw(284)} ${desktopVw(100)} ${desktopVw(296)};

    h1 {
      font-size: ${desktopVw(24)};
      line-height: ${desktopVw(32)};
      padding-bottom: ${desktopVw(33)};
      margin-bottom: ${desktopVw(4)};
    }

    h3 {
      font-size: ${desktopVw(16)};
      line-height: ${desktopVw(22)};
      margin: ${desktopVw(20)} 0 ${desktopVw(29)};
    }

    p {
      font-size: ${desktopVw(13)};
      line-height: ${desktopVw(19)};
      margin: 0 0 ${desktopVw(30)};
      max-width: ${desktopVw(740)};
    }
  }
`

function updateChatbotMsg(gorgiasChat) {
  const privacyPolicyLink = '/privacy-policy'

  const introMessage = `For quality assurance purposes, your chat session is recorded [and/or] may be monitored or reviewed.
    By joining this chat, you agree to our <a href="${privacyPolicyLink}" target="_blank">Privacy Policy</a>.
  `

  gorgiasChat.updateTexts({
    requireEmailCaptureIntro: `Hey there! ${introMessage}`,
    contactFormIntro: `Hey there! ${introMessage}`,
    contactFormSSPUnsuccessfulAskAdditionalMessage: `Hey there! ${introMessage}`,
    waitTimeLongNoEmail: `Thank you for your patience. ${introMessage}`,
    waitTimeMediumNoEmail: `Thanks for reaching out! Leave us your email and an agent will get back to you in about {waitTime} minutes. ${introMessage}`,
    waitTimeShortNoEmail: `Thanks for reaching out! ${introMessage}`,
  })

  gorgiasChat.updateSSPTexts({
    sorryToHearThatEmailRequired: introMessage,
  })
}

function init(retryCount = 20) {
  const chat = window.GorgiasChat
  if (chat) {
    chat.init().then(gorgiasChat => {
      updateChatbotMsg(gorgiasChat)
    })
  } else if (retryCount > 0) {
    setTimeout(() => {
      init(retryCount - 1)
    }, 1000)
  }
}

const useChat = () => {
  useEffect(() => {
    const scriptElement = document.querySelector('#gorgias-chat-widget-install-v3')
    if (scriptElement) {
      document.querySelector('#gorgias-chat-container')?.classList.remove('d-none')

      return () => {
        document.querySelector('#gorgias-chat-container')?.classList.add('d-none')
      }
    }

    const scriptTag = document.createElement('script')

    scriptTag.id = 'gorgias-chat-widget-install-v3'
    scriptTag.src = `https://config.gorgias.chat/bundle-loader/01GYCC1RTXQ09J5ZGW24QMCKBS`
    document.head.appendChild(scriptTag)
    scriptTag.onload = function () {
      init()
    }

    return () => {
      document.querySelector('#gorgias-chat-container')?.classList.add('d-none')
    }
  }, [])

  const openChatbot = () => {
    try {
      const chat = window.GorgiasChat
      if (!chat.isBusinessHours()) {
        return
      }
      if (chat.isOpen()) {
        chat.close()
      } else {
        trackEvent(TELEMETRY_EVENT_NAMES.CHAT_WITH_US_CLICKED, {})
        chat.open()
      }
    } catch (e) {
      console.log('Error while opening chat, openChatbot() runtime:: ', e)
    }
  }
  return openChatbot
}

function ContactUs({ data: { landingPage: { sections, faq } = {} } = {} }) {
  const openChat = useChat()
  const textSections = sections.filter(section => section.__typename === 'ContentfulTextSection')
  const svgSections = sections.filter(section => section.__typename === 'ContentfulSvgWithText')

  const [contactUsText, chatWithUsText, ...marketingTexts] = textSections
  const orderSvgs = svgSections.slice(0, 2)

  return (
    <Section className="contact-us">
      {sections.map(s => {
        switch (s.__typename) {
          case 'ContentfulBanner':
            return (
              <>
                <BannerSection contactUsText={contactUsText} orderSvgs={orderSvgs} section={s} />
                <ChatFAQContainer>
                  <MarketingSection marketingTexts={[chatWithUsText, ...marketingTexts]} onChat={openChat} />
                  {faq && (
                    <FAQWrapper>
                      <h1>FAQ</h1>
                      {faq.map(faqItem => (
                        <Collapsible
                          key={faqItem.title}
                          title={<h3>{faqItem.title}</h3>}
                          className={styles.collapsibleContainer}
                          startCollapsed
                        >
                          {richTextRenderer(faqItem.text.json)}
                        </Collapsible>
                      ))}
                    </FAQWrapper>
                  )}
                </ChatFAQContainer>
              </>
            )
          default:
            return null
        }
      })}
    </Section>
  )
}

export default ContactUs
